import React from 'react';
import { Book, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAppStore } from '@/store';
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';

interface KnowledgeBaseActiveIndicatorProps {
  knowledgeBaseName: string | null;
  onClear: () => void;
}

const KnowledgeBaseActiveIndicator: React.FC<KnowledgeBaseActiveIndicatorProps> = ({
  knowledgeBaseName,
  onClear
}) => {
  if (!knowledgeBaseName) return null;

  const { knowledgeBases, fetchKnowledgeBases } = useKnowledgeBases();

  React.useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  const getKnowledgeBaseName = (kbId: string) => {
    const kb = knowledgeBases.find(kb => kb.id === kbId);
    return kb?.name || 'Unknown Knowledge Base';
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className="flex items-center gap-1.5 border-violet-300 bg-violet-200 text-violet-950 shadow-sm dark:border-violet-500/40 dark:bg-violet-500/20 dark:text-violet-100"
          >
            <Book size={14} className="text-violet-800 dark:text-violet-200" />
            <span className="max-w-[220px] truncate">{knowledgeBaseName}</span>
            <Button
              variant="ghost"
              size="icon"
              className="ml-1 h-4 w-4 rounded-full p-0 text-violet-800 hover:bg-violet-300/80 hover:text-violet-950 dark:text-violet-200 dark:hover:bg-violet-400/25 dark:hover:text-violet-50"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
            >
              <X size={12} />
            </Button>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>Knowledge base active - click X to remove</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default KnowledgeBaseActiveIndicator;